import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check } from "lucide-react";

import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/ui/button";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { useSubscriptionCheckout } from "../../../SubscriptionCheckout/SubscriptionCheckoutContext";
import {
  getSubscriptionOffering,
  type SubscriptionOffering,
} from "../../api/getSubscriptionOffering";

function formatPrice(amount: string): string {
  // Server hands us a decimal string ("175.00"); we keep it that way for
  // money math but render with a $ + grouping for the UI.
  const num = Number.parseFloat(amount);
  if (Number.isNaN(num)) return amount;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPerLabel(frequency: string): string {
  switch (frequency) {
    case "weekly":
      return "per week";
    case "monthly":
      return "per month";
    case "quarterly":
      return "per quarter";
    case "annual":
    case "yearly":
      return "per year";
    default:
      return `per ${frequency}`;
  }
}

export function TotalAccessLandingPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { setSelection } = useSubscriptionCheckout();

  const [offering, setOffering] = useState<SubscriptionOffering | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getSubscriptionOffering(slug)
      .then((response) => {
        if (cancelled) return;
        setOffering(response.data);
        setSelected(response.data.pricingOptions[0]?.frequency ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setError(
          "We couldn't load the subscription details. Please try again.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleContinue = () => {
    if (!selected || !slug || !offering) return;
    const option = offering.pricingOptions.find(
      (opt) => opt.frequency === selected,
    );
    if (!option) return;
    setSelection({
      offeringName: offering.name,
      caraSku: offering.caraSku,
      frequency: option.frequency,
      unitPrice: Number.parseFloat(option.amount),
      label: option.label ?? option.frequency,
    });
    navigate(`/subscriptions/${slug}/checkout`);
  };

  if (error) {
    return (
      <MainPageWrapper subtitle={undefined} title="Total Access">
        <p
          className="text-sm text-red-600 dark:text-red-400"
          data-testid="offering-error"
        >
          {error}
        </p>
      </MainPageWrapper>
    );
  }

  if (!offering) {
    return (
      <MainPageWrapper subtitle={undefined} title="Total Access">
        <LoadingIndicator />
      </MainPageWrapper>
    );
  }

  return (
    <MainPageWrapper
      subtitle={offering.subheading ?? undefined}
      title={offering.name}
    >
      <div className="max-w-4xl mx-auto space-y-8">
        {offering.description ? (
          <p className="text-base text-gray-700 dark:text-gray-300">
            {offering.description}
          </p>
        ) : null}

        {offering.videoUrl ? (
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden bg-black">
            <video
              className="w-full"
              controls
              data-testid="total-access-video"
              preload="metadata"
              src={offering.videoUrl}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        ) : null}

        {offering.whatsIncluded.length > 0 ? (
          <section>
            <h2 className="text-base font-normal text-gray-900 dark:text-white mb-3">
              What&apos;s included
            </h2>
            <ul className="space-y-2">
              {offering.whatsIncluded.map((benefit) => (
                <li
                  className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300"
                  key={benefit}
                >
                  <Check className="h-4 w-4 mt-0.5 text-gray-500 dark:text-gray-400 shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {offering.alreadySubscribed ? (
          <section
            className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3"
            data-testid="already-subscribed-banner"
          >
            <p className="text-sm text-emerald-800 dark:text-emerald-200">
              You&apos;re already subscribed to {offering.name}. Your benefits
              are active.
            </p>
          </section>
        ) : null}

        {offering.pricingOptions.length > 0 && !offering.alreadySubscribed ? (
          <section>
            <h2 className="text-base font-normal text-gray-900 dark:text-white mb-3">
              Choose a billing frequency
            </h2>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              data-testid="frequency-picker"
              role="radiogroup"
            >
              {offering.pricingOptions.map((option) => {
                const isSelected = selected === option.frequency;
                return (
                  <button
                    aria-checked={isSelected}
                    className={`text-left rounded-lg border p-4 transition-colors ${
                      isSelected
                        ? "border-primary bg-primary/5 dark:bg-primary/10"
                        : "border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600"
                    }`}
                    data-testid={`frequency-option-${option.frequency}`}
                    key={option.frequency}
                    onClick={() => setSelected(option.frequency)}
                    role="radio"
                    type="button"
                  >
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {option.label ?? option.frequency}
                    </p>
                    <p className="text-2xl font-normal text-gray-900 dark:text-white mt-1">
                      {formatPrice(option.amount)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formatPerLabel(option.frequency)}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {offering.footer ? (
          <section className="text-xs text-gray-500 dark:text-gray-400">
            <p>{offering.footer}</p>
          </section>
        ) : null}

        {!offering.alreadySubscribed ? (
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button
              data-testid="continue-button"
              disabled={!selected}
              onClick={handleContinue}
              type="button"
            >
              Continue to checkout
            </Button>
          </div>
        ) : null}
      </div>
    </MainPageWrapper>
  );
}
